import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { Button, TableItem } from 'components/atoms';
import { FormInput, LabeledStatus } from 'components/molecules';
import { QueryFilter, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useComponentVisible, useHasPermission } from 'hooks';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { t } from 'i18next';
import { useState } from 'react';
import {
  PiBuildingOffice,
  PiIdentificationCard,
  PiUserCircle,
} from 'react-icons/pi';
import { useNavigate } from 'react-router';
import { useGetLabelListInfinite } from 'services/labels';
import {
  CUSTOMER_TYPE,
  IGetLabelListInfiniteParams,
  ILabelResponse,
} from 'services/labels/types';
import { STATUS } from 'services/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';
import { getNodes } from 'utils/table/nodes.utils';
import { useSearchById, useSetSearchById } from 'store/state';

enum LABEL_COLUMNS {
  labelName,
  fullname,
  contact,
  customerType,
  status,
}

const initialValues: IGetLabelListInfiniteParams = {
  labelName: '',
  personalId: '',
  companyId: '',
  email: '',
  status: undefined,
};

const Labels = () => {
  usePermissionGuard('LabelPermissionsLIST**');
  const hasPermission = useHasPermission();

  const columnsVisibility = useTableColumnsVisibility('Labels', LABEL_COLUMNS);
  const navigate = useNavigate();
  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false);
  const i18nextPrefix = 'labels';

  const searchById = useSearchById();
  const setSearchById = useSetSearchById();
  const filters = searchById[columnsVisibility.tableId] || initialValues;

  const { data, isLoading, isFetchingNextPage, fetchNextPage } =
    useGetLabelListInfinite(filters);
  const tableData = getNodes<ILabelResponse>(data?.pages.flat());

  const hasDetailPermission = hasPermission('LabelPermissionsLIST**', 16);

  const onSelectChange = (action: Action, state: State) => {
    if (!hasDetailPermission) {
      return;
    }
    
    navigate(`${state.id ?? state.ids[state.ids.length - 1]}/finances`);
  };

  const select = useRowSelect(tableData, {
    onChange: onSelectChange,
  });

  const tableSelect = hasDetailPermission ? select : undefined;

  const { getSelectOptions } = useEnumToSelectOptions();

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t('label.title.list')}
          path=''
          hideBackButton
          topbarContent={
            <>
              <div className='flex'>
                <QueryFilter.ButtonOpener
                  i18nextPrefix={i18nextPrefix}
                  onClick={handleComponentVisible}
                  variant={BUTTON_VARIANT.default}
                  isClear
                />
                <Table.CustomColumnsButton
                  columns={LABEL_COLUMNS}
                  i18nextPrefix='label'
                  isClear
                  visibility={columnsVisibility}
                />
              </div>
              {hasPermission('LabelPermissionsLIST**', 1) && (
                <Button
                  variant={BUTTON_VARIANT.submit}
                  onClick={() => navigate('create/finances', { replace: false })}>
                  <Button.Text
                  text={t('button.create-what', { what: t('label.what') })}
                />
                  <Button.Icon icon={<PiIdentificationCard />} />
                </Button>
              )}
            </>
          }>
          <QueryFilter<IGetLabelListInfiniteParams>
            i18nextPrefix={i18nextPrefix}
            initialValues={filters}>
            <>
              <QueryFilter.Form<IGetLabelListInfiniteParams>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={values => setSearchById(columnsVisibility.tableId, values)}>
                <QueryFilter.InputsGrid>
                  <FormInput.Select
                    name='status'
                    label={t('input.label.status')}
                    options={getSelectOptions(STATUS, 'enum.STATUS')}
                  />
                  <FormInput.Text
                    name='labelName'
                    label={t('input.label.name')}
                  />
                  <FormInput.Text name='email' label={t('input.label.email')} />
                  <FormInput.Text
                    name='personalId'
                    label={t('input.label.personalId')}
                  />
                  <FormInput.Text
                    name='companyId'
                    label={t('input.label.companyId')}
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilter.Labels
                isFormOpen={isComponentVisible}
                customLabel={data =>
                  data.key === 'status'
                    ? t(`enum.STATUS.${STATUS[data.value as STATUS]}`)
                    : `${data.value}`
                }
              />
            </>
          </QueryFilter>
        </TopBar>
      }>
      <Table
        id={columnsVisibility.tableId}
        select={tableSelect}
        data={tableData}
        loading={isLoading || isFetchingNextPage}
        onFullyScroll={fetchNextPage}
        columns={[
          {
            label: t('label.table.labelName'),
            renderCell: ({ labelName }) => (
              <TableItem>
                <TableItem.Text text={labelName} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(LABEL_COLUMNS.labelName),
          },
          {
            label: t('label.table.fullname'),
            renderCell: ({ firstName, lastName }) => (
              <TableItem>
                <TableItem.Text
                  text={
                    firstName && lastName
                      ? `${firstName} ${lastName}`
                      : undefined
                  }
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(LABEL_COLUMNS.fullname),
          },
          {
            label: t('label.table.contact'),
            renderCell: ({ email, phoneNumberNumber, phoneNumberPrefix }) => (
              <TableItem className='gap-2'>
                <TableItem.Text text={email} />
                {phoneNumberNumber && phoneNumberPrefix ? (
                  <TableItem.Text
                    text={`${phoneNumberPrefix} ${phoneNumberNumber}`}
                    isSupplementary
                  />
                ) : null}
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(LABEL_COLUMNS.contact),
          },
          {
            label: t('label.table.customerType'),
            renderCell: ({ customerTypeId }) => (
              <TableItem justifyPosition='center'>
                {customerTypeId === CUSTOMER_TYPE.Company && (
                  <TableItem.Label color='blue' size='compact'>
                    <PiBuildingOffice />
                    {t('input.label.company')}
                  </TableItem.Label>
                )}
                {customerTypeId === CUSTOMER_TYPE.Physical && (
                  <TableItem.Label color='black' size='compact'>
                    <PiUserCircle />
                    {t('input.label.physicalPerson')}
                  </TableItem.Label>
                )}
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(LABEL_COLUMNS.customerType),
          },
          {
            label: t('label.table.status'),
            renderCell: ({ labelStatus }) => (
              <TableItem justifyPosition='center'>
                <LabeledStatus status={labelStatus} />
              </TableItem>
            ),
            hide: columnsVisibility.hide(LABEL_COLUMNS.status),
          },
        ]}
        displayMode
      />
    </TopbarPage>
  );
};

export default Labels;
